import { type Locale } from '@syncanix/shared';
import { cn } from '@syncanix/ui';

import { LinkPanel } from '@/components/bedrock';
import { isolatePaths } from '@/lib/bidi/isolate-paths';

import { renderInline } from './doc-inline';
import { resolveDocLink } from './doc-links';
import { plannedPackagesIn } from './install-availability';

import type { DocBlock } from './content/types';
import type { Translator } from '@/i18n/translator';
import type { ReactElement } from 'react';

/**
 * Renders the typed documentation block model (ADR-0074) to React. Tokenized
 * Tailwind only, logical/RTL-correct (no physical directional classes), built to
 * read cleanly in all six locales. Code blocks are forced LTR so snippets stay
 * legible on RTL pages.
 */
export function DocBlocks({
  blocks,
  t,
  locale,
}: {
  readonly blocks: readonly DocBlock[];
  readonly t: Translator;
  readonly locale: Locale;
}): ReactElement {
  return (
    // `break-words` (overflow-wrap: break-word) because documentation prose
    // routinely carries an unbreakable run — a long identifier, a URL, a
    // snippet quoted inline. One of them
    // (`webviewRef.injectJavaScript(...)` on /docs/widget/mobile-webview)
    // forced its paragraph 117px past the column at 390px and scrolled the
    // WHOLE PAGE sideways. It breaks only when the word alone does not fit,
    // so ordinary prose is unaffected.
    <div className="flex flex-col gap-6 break-words text-[1.02rem] leading-[1.68] text-[color:var(--bd-body)]">
      {blocks.map((block, i) => (
        <Block key={i} block={block} t={t} locale={locale} />
      ))}
    </div>
  );
}

/**
 * Callout tones, redesigned against the bedrock language.
 *
 * Each is a hairline card carrying a 3px accent rule on its inline-start edge —
 * the same device the story band's result cards use for their top rule, turned
 * on its side. The tones read as a ramp rather than as four unrelated colours:
 * neutral, brand-cyan, brand-blue, danger. `border-s-[3px]` rather than `-4`
 * because the card now has a hairline all the way round, so the accent only has
 * to be legible, not structural.
 */
const CALLOUT_TONE: Record<string, string> = {
  note: 'border-[color:var(--bd-line)] border-s-[color:var(--bd-dot)] bg-[color:var(--bd-sunk)]',
  tip: 'border-[color:var(--bd-line)] border-s-[color:var(--bd-cyan)] bg-[rgb(22_210_255/0.05)]',
  important:
    'border-[color:var(--bd-line)] border-s-[color:var(--bd-blue)] bg-[rgb(43_98_255/0.05)]',
  caution:
    'border-[color:var(--bd-line)] border-s-[color:var(--bd-warn)] bg-[rgb(245_158_11/0.06)]',
};

function Block({
  block,
  t,
  locale,
}: {
  readonly block: DocBlock;
  readonly t: Translator;
  readonly locale: Locale;
}): ReactElement | null {
  switch (block.kind) {
    case 'heading': {
      const Tag = block.level === 2 ? 'h2' : 'h3';
      return (
        <Tag
          id={block.id}
          className={cn(
            'font-semibold text-[color:var(--bd-ink)]',
            // `scroll-mt` clears the sticky bar on an in-page anchor jump; the
            // token is the one the bar's own height is authored from.
            'scroll-mt-[calc(var(--bd-nav-h)+22px)]',
            block.level === 2
              ? 'mt-6 text-[1.55rem] leading-[1.15] tracking-[-0.034em]'
              : 'mt-3 text-[1.2rem] leading-[1.25] tracking-[-0.026em]',
          )}
        >
          {isolatePaths(t(block.textKey))}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p>{renderInline(t(block.textKey, block.params?.(locale)), block.links, t, locale)}</p>
      );
    case 'list': {
      const ListTag = block.ordered === true ? 'ol' : 'ul';
      return (
        <ListTag
          className={cn(
            'flex flex-col gap-2 ps-6 marker:text-[color:var(--bd-dot)]',
            block.ordered === true ? 'list-decimal' : 'list-disc',
          )}
        >
          {block.itemKeys.map((key) => (
            <li key={key} className="ps-1">
              {isolatePaths(t(key))}
            </li>
          ))}
        </ListTag>
      );
    }
    case 'steps':
      return (
        <ol className="flex flex-col gap-4">
          {block.steps.map((step, i) => (
            <li key={step.titleKey} className="flex gap-3">
              <span
                aria-hidden="true"
                className="grid size-7 shrink-0 place-items-center rounded-[9px] font-mono text-[11.5px] font-medium text-white"
                style={{ background: 'var(--bd-gradient-ink)' }}
              >
                {i + 1}
              </span>
              <div className="flex flex-col gap-1">
                <span className="font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
                  {isolatePaths(t(step.titleKey))}
                </span>
                {step.textKey !== undefined ? (
                  <span className="text-[color:var(--bd-muted)]">
                    {isolatePaths(t(step.textKey))}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      );
    case 'callout':
      return (
        <aside
          className={cn(
            'rounded-[14px] border border-s-[3px] px-4 py-3.5',
            CALLOUT_TONE[block.tone] ?? CALLOUT_TONE.note,
          )}
        >
          {block.titleKey !== undefined ? (
            <p className="mb-1.5 text-[0.95rem] font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
              {isolatePaths(t(block.titleKey))}
            </p>
          ) : null}
          <p className="text-[0.9rem] leading-[1.62] text-[color:var(--bd-body)]">
            {renderInline(t(block.textKey, block.params?.(locale)), block.links, t, locale)}
          </p>
        </aside>
      );
    case 'code':
      return (
        <figure className="flex flex-col gap-2">
          {/* `tabIndex` because the block scrolls horizontally and a keyboard
              otherwise cannot pan it (WCAG 2.1.1 / axe
              `scrollable-region-focusable`). Deliberately NOT `role="region"`:
              a code sample has no name to give one, and an unnamed region is
              worse than none — `<pre>` already conveys preformatted text. */}
          <pre
            dir="ltr"
            tabIndex={0}
            className="overflow-x-auto rounded-[13px] bg-[color:var(--bd-ink)] p-5 text-start text-[0.86rem] leading-[1.7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2"
          >
            <code className="font-mono text-[color:var(--bd-band-ink)]">{block.code}</code>
          </pre>
          {/* B27(6) install-command truthfulness: while a referenced package
              is not yet live on its registry (D27 SoT), say so — the command
              would error for a real customer today. SoT-driven: the note
              clears itself when D27 flips the package to `live`. */}
          {plannedPackagesIn(block.code).length > 0 ? (
            <figcaption className="text-xs font-medium text-link" data-install-availability>
              {t('docs.install.availableAtLaunch')}
            </figcaption>
          ) : null}
          {block.captionKey !== undefined ? (
            <figcaption className="text-xs text-[color:var(--bd-faint)]">
              {isolatePaths(t(block.captionKey))}
            </figcaption>
          ) : null}
        </figure>
      );
    case 'cardGrid':
      return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {block.cards.map((card, index) => {
            const { href, external } = resolveDocLink(card.to, locale);
            // An ODD card count leaves the bottom-right cell empty — a
            // card-sized hole at the end of the grid, which fourteen doc pages
            // shipped with (three cards is the common shape: two side by side
            // and a ~430px void beside the third). The last card of an odd set
            // spans both tracks instead, so the grid always ends on a full row.
            // Expressed as a property of the COUNT rather than as a per-page
            // fix, since the counts are authored per page and will keep moving.
            const spansRow = block.cards.length % 2 === 1 && index === block.cards.length - 1;
            return (
              <LinkPanel
                key={card.titleKey}
                href={href}
                external={external}
                className={spansRow ? 'p-5 sm:col-span-2' : 'p-5'}
              >
                <span className="block font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
                  {isolatePaths(t(card.titleKey))}
                </span>
                <span className="mt-1.5 block text-[0.9rem] leading-[1.6] text-[color:var(--bd-muted)]">
                  {isolatePaths(t(card.textKey))}
                </span>
              </LinkPanel>
            );
          })}
        </div>
      );
    case 'definitions':
      return <Definitions block={block} t={t} />;
    default:
      return null;
  }
}

/**
 * A term/definition list.
 *
 * Rows are hairline-divided rather than merely gapped. Stacked pairs on a `gap`
 * alone have no row boundary, so a fourteen-flag options list reads as an
 * undifferentiated wall — and each bold term followed by a grey line looks like
 * a heading with a paragraph under it, competing with the page's real headings.
 * The divider is what makes a row a row.
 *
 * The `code` variant additionally goes two-column from `sm` up, which is the
 * shape a reference table wants: the eye scans the mono column for the flag it
 * came for instead of reading every description to find it. It collapses back to
 * stacked on mobile, where a 13.5rem term column would leave the description a
 * few words wide.
 */
function Definitions({
  block,
  t,
}: {
  readonly block: Extract<DocBlock, { kind: 'definitions' }>;
  readonly t: Translator;
}): ReactElement {
  const code = block.variant === 'code';
  return (
    <dl className="flex flex-col">
      {block.items.map((item, i) => (
        <div
          key={item.termKey}
          className={cn(
            'py-3.5 first:pt-0 last:pb-0',
            i > 0 && 'border-t border-[color:var(--bd-line)]',
            code ? 'sm:flex sm:items-baseline sm:gap-6' : 'flex flex-col gap-1',
          )}
        >
          {code ? (
            // `dir="ltr"` + `text-start`: a flag, path or field name is a literal
            // token, so its characters must not be reordered on an RTL page —
            // the same rule the code block above follows. `break-words` because
            // a long path offers no space to wrap at.
            <dt
              dir="ltr"
              className="shrink-0 break-words text-start font-mono text-[0.86rem] leading-[1.5] text-[color:var(--bd-ink)] sm:w-[13.5rem]"
            >
              {t(item.termKey)}
            </dt>
          ) : (
            <dt className="font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
              {t(item.termKey)}
            </dt>
          )}
          <dd
            className={cn(
              'text-[color:var(--bd-body)]',
              code && 'mt-1 text-[0.95rem] leading-[1.62] sm:mt-0 sm:flex-1',
            )}
          >
            {isolatePaths(t(item.descKey))}
          </dd>
        </div>
      ))}
    </dl>
  );
}
